import { SpreadType } from './exchange';
import { File } from './file';

export type User = {
  id: string;
  externalId?: string; // ID from external systems (e.g., SmartFin)
  authId: string;
  name: string;
  email: string;
  entityType?: UserEntityType;
  documentNumber?: string;
  phone?: string;
  birthDate?: string | null;
  country?: string;
  addressStreet?: string;
  addressNumber?: string;
  addressComplement?: string;
  addressNeighborhood?: string;
  addressCity?: string;
  addressState?: string;
  addressZipCode?: string;
  acceptedInvite: boolean;
  role: UserRole;
  spreadType?: SpreadType;
  spread: number;
  transactionLimit?: number | null;
  dailyLimit?: number | null;
  monthlyLimit?: number | null;
  walletAddress?: string | null;
  balance?: number | null;
  notificationTokensWeb?: NotificationTokensWeb[];
  inviteSentAt?: Date;
  acceptedTermsAt?: Date;
  recoveryCodes?: RecoveryCode[];
  createdAt: Date;
  updatedAt?: Date;
  deletedAt: Date | null;
  limitIncreaseApproved?: boolean;
  documentsApproved?: boolean | null; // Admin approval for basic documents
  documentsRequested?: boolean | null;
  requestedTransactionAmount?: number | null;
  pixKey?: string;

  documentsRequestedAt?: Date | null; // When the user was requested to send documents
  documentsSubmittedAt?: Date | null; // When the user submitted the documents
  documentsApprovedAt?: Date | null; // When the documents were approved by admin
  limitIncreaseRequestedAt?: Date | null; // When the user requested a limit increase
  limitIncreaseApprovedAt?: Date | null; // When the transaction limit was last updated

  // Business (Corporate Banking)
  businessRegistrationCertificate?: File | null; // Comprovante de CNPJ
  articlesOfAssociation?: File | null; // Contrato Social ou Estatuto Social
  shareholdersAndDirectorsID?: File[] | null; // Documentos de Identificação de Sócios e Administradores
  annualFinancialStatements?: File | null; // Demonstrações Financeiras Anuais
  businessProofOfAddress?: File | null; // Comprovante de Endereço da empresa
  businessActivityStatement?: File | null; // Declaração de Atividade Econômica (descrevendo as principais operações da empresa)
  isRegulatedEntity?: boolean; // A empresa é uma entidade regulamentada? (instituição financeira, casa de câmbio, cooperativa, etc.)
  amlCftInternalPolicies?: File | null; // Políticas internas de PLD/CFT (se a empresa for uma entidade regulamentada, como uma instituição financeira, casa de câmbio ou cooperativa)

  // Individual (Personal Banking)
  photoId?: File | null; // Cópia de documento com foto ( CNH ou RG )
  proofOfAddress?: File | null; // Comprovante de Residência
  proofOfIncomeOrActivity?: File | null; // Comprovantes de Renda ou Atividade Econômica
  recentPayslips?: File[] | null; // Holerites ou contracheques dos últimos meses
  incomeTaxReturn?: File | null; // Declaração de Imposto de Renda (IR), especialmente para clientes de alta renda
  recentBankStatements?: File[] | null; // Extratos bancários recentes, demonstrando movimentação financeira
  proofOfEmployment?: File | null; // Comprovantes de vínculo empregatício, como carteira de trabalho digital ou contrato de prestação de serviço.

  // External API Data
  apiKey?: string;
  webhookUrl?: string;
  webhookSecret?: string;
};

export type UserFormData = User & {
  password?: string;
};

export enum UserRole {
  Admin = 'admin',
  User = 'user',
  Commercial = 'commercial',
}

export enum InviteStatus {
  Pending = 'PENDING',
  Accepted = 'ACCEPTED',
  Expired = 'EXPIRED',
}

export enum UserEntityType {
  Natural = 'NATURAL',
  Legal = 'LEGAL',
}

export type NotificationTokensWeb = {
  token: string;
  userAgent: string;
  createdAt: string;
  deactivatedAt?: string;
  successCount: number;
  failCount: number;
  lastSuccessAt?: string;
  lastFailAt?: string;
};

export type RecoveryCode = {
  code: string;
  expiresAt: Date;
};

const USER_ENTITY_TYPE_LABELS: Record<UserEntityType, string> = {
  [UserEntityType.Natural]: 'Pessoa Física',
  [UserEntityType.Legal]: 'Pessoa Jurídica',
};

export const USER_ENTITY_TYPE_OPTIONS = Object.entries(USER_ENTITY_TYPE_LABELS).map(
  ([key, label]) => ({
    value: key as UserEntityType,
    label,
  }),
);

export function getUserEntityTypeLabel(status: UserEntityType): string {
  return USER_ENTITY_TYPE_LABELS[status] || status;
}
